/**
 * SyncIndicator — Property sync health indicator
 *
 * Polls GET /api/sync-status/{property_id} every 15 seconds.
 * Shows: green (OK), yellow (DELAYED), red (FAILED)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { getToken } from '../services/tokenService';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const POLL_INTERVAL = 15000; // 15 seconds

const STATUS_CONFIG = {
  OK: {
    dot: 'bg-emerald-400',
    pulse: 'bg-emerald-400/40',
    label: 'Sync OK',
    textColor: 'text-emerald-300',
  },
  DELAYED: {
    dot: 'bg-yellow-400',
    pulse: 'bg-yellow-400/40',
    label: 'Sync Delayed',
    textColor: 'text-yellow-300',
  },
  FAILED: {
    dot: 'bg-red-500',
    pulse: 'bg-red-500/40',
    label: 'Sync Failed',
    textColor: 'text-red-300',
  },
  LOADING: {
    dot: 'bg-gray-400',
    pulse: 'bg-gray-400/40',
    label: 'Checking...',
    textColor: 'text-gray-400',
  },
};

const SyncIndicator = ({ propertyId }) => {
  const [syncData, setSyncData] = useState(null);
  const [status, setStatus] = useState('LOADING');
  const [expanded, setExpanded] = useState(false);
  const intervalRef = useRef(null);

  const fetchSyncStatus = useCallback(async () => {
    if (!propertyId) {
      // No property → never stay on "Checking..."; assume OK
      setStatus('OK');
      return;
    }
    // 10-second hard timeout — never leave the user on "Checking..."
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10_000);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/sync-status/${propertyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.STATUS === 1 && data.RESULT) {
          setSyncData(data.RESULT);
          setStatus(data.RESULT.sync_status || 'OK');
          return;
        }
      }
      // Non-2xx or unexpected payload → fall through to OK fallback
      setStatus('OK');
    } catch (err) {
      // Timeout / network error → never stay on LOADING
      setStatus(err.name === 'AbortError' ? 'OK' : 'OK');
    } finally {
      clearTimeout(timeoutId);
    }
  }, [propertyId]);

  // Initial fetch + polling
  useEffect(() => {
    fetchSyncStatus();
    intervalRef.current = setInterval(fetchSyncStatus, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchSyncStatus]);

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.LOADING;

  return (
    <div className="relative" data-testid="sync-indicator">
      {/* Compact dot + label */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-navy-light/50 hover:bg-navy-light transition-colors"
        data-testid="sync-indicator-btn"
        title={cfg.label}
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${cfg.pulse}`} />
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.dot}`} data-testid="sync-status-dot" />
        </span>
        {/* iter137 — Text label removed per product ask. The green/yellow/red
            dot itself is now the entire sync indicator; the full label still
            surfaces in the native tooltip (title attr) + expanded panel. */}
      </button>

      {/* Expanded details dropdown */}
      {expanded && syncData && (
        <div
          className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
          data-testid="sync-details-panel"
        >
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Sync Details</h4>
            <button onClick={fetchSyncStatus} className="text-gray-400 hover:text-gray-600" data-testid="sync-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-4 py-3 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className={`font-semibold ${
                status === 'OK' ? 'text-emerald-600' :
                status === 'DELAYED' ? 'text-yellow-600' : 'text-red-600'
              }`} data-testid="sync-detail-status">{status}</span>
            </div>
            {/* iter138 — CRM Leads + CRM Updated rows removed per product ask.
                Sync itself continues untouched; only the labels are hidden. */}
            <div className="flex justify-between">
              <span className="text-gray-500">Bookings</span>
              <span className="font-medium text-gray-900" data-testid="sync-bookings-count">{syncData.app_bookings_count}</span>
            </div>
            {syncData.last_app_update && (
              <div className="flex justify-between">
                <span className="text-gray-500">Booking Updated</span>
                <span className="font-medium text-gray-900 text-[10px]">
                  {new Date(syncData.last_app_update).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                </span>
              </div>
            )}
            {syncData.reason && (
              <div className="pt-1 border-t border-gray-100">
                <span className="text-gray-400">{syncData.reason}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncIndicator;
